/**
 * Dashboard shell: responsive sidebar + header + outlet + footer.
 *
 * - Desktop (md+): collapsible rail (w-60 ↔ w-16); collapsed shows icon + tooltip.
 * - Mobile (<md): off-canvas drawer opened by the header hamburger (overlay + backdrop).
 * - Header: command-palette trigger, theme toggle, user dropdown (Settings + Déconnexion).
 * - Footer: product name, version, runtime env indicator, status dot.
 *
 * Fully themed via the semantic design tokens (bg/surface/border/…) so light
 * and dark are driven by the `.dark` class the UI store toggles.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { useAuth } from 'react-oidc-context';

import { useT, type TranslationKey } from '@/i18n';
import { CommandPalette } from '@/app/CommandPalette';
import { ShortcutsOverlay, useGlobalShortcuts } from '@/app/keyboard-shortcuts';
import { RouteErrorBoundary } from '@/app/RouteErrorBoundary';
import { UpdateBanner } from '@/app/UpdateBanner';
import { getConfig } from '@/config';
import { CopilotProvider } from '@/features/copilot';
import { DemoTour } from '@/features/demo/DemoTour';
import { NotificationBell } from '@/routes/notifications/NotificationBell';
import { useUIStore } from '@/stores/ui-store';
import { GlobalConfirmHost } from '@/ui';
import { ThemePicker } from '@/ui/ThemePicker';
import { Toaster } from '@/ui/toast';

// Build version — injected at build time (see vite.config.ts). Same across
// dev/QA/prod; full provenance at /versionsbuilt.
const APP_VERSION = __BUILD_META__.version;

// ---------------------------------------------------------------------------
// Inline SVG icons (zero-dep). Common 24x24 stroke paths, sized by className.
// ---------------------------------------------------------------------------

type IconName =
  | 'dashboard'
  | 'content'
  | 'calendar'
  | 'publishing'
  | 'platforms'
  | 'media'
  | 'analytics'
  | 'design'
  | 'workflows'
  | 'crm'
  | 'outreach'
  | 'inbox'
  | 'projects'
  | 'ai-assistant'
  | 'ai-team'
  | 'orchestration'
  | 'studio'
  | 'repurpose'
  | 'hooks'
  | 'voice'
  | 'ab-test'
  | 'suggestions'
  | 'personalization'
  | 'community'
  | 'documents'
  | 'memory'
  | 'approvals'
  | 'ai-activity'
  | 'marketplace'
  | 'prospect'
  | 'discovery'
  | 'social'
  | 'listening'
  | 'ads'
  | 'bio'
  | 'settings';

const ICON_PATHS: Record<IconName, ReactNode> = {
  dashboard: <path d="M4 4h7v7H4zM13 4h7v4h-7zM13 10h7v10h-7zM4 13h7v7H4z" />,
  content: <path d="M8 4h8l4 4v12H4V4zM14 4v4h4M8 13h8M8 17h5" />,
  calendar: <path d="M4 5h16v15H4zM4 9h16M8 3v4M16 3v4" />,
  publishing: <path d="m4 12 16-8-6 16-3-6-7-2z" />,
  platforms: <path d="M7 12a5 5 0 0 1 5-5m0 10a5 5 0 0 0 5-5M6 8a2 2 0 1 0 0-4 2 2 0 0 0 0 4zM18 8a2 2 0 1 0 0-4 2 2 0 0 0 0 4zM12 22a2 2 0 1 0 0-4 2 2 0 0 0 0 4z" />,
  media: <path d="M4 5h16v14H4zM4 15l4-4 5 5M14 13l2-2 4 4M9 9a1 1 0 1 1-2 0 1 1 0 0 1 2 0z" />,
  analytics: <path d="M5 20V10M12 20V4M19 20v-7M3 20h18" />,
  design: <path d="M12 3a9 9 0 1 0 0 18h1.5a2 2 0 0 0 1.6-3.2 2 2 0 0 1 1.6-3.2H19a3 3 0 0 0 3-3A9 9 0 0 0 12 3zM7.5 11.5a1 1 0 1 1 0-.01M12 8a1 1 0 1 1 0-.01M16 11a1 1 0 1 1 0-.01" />,
  workflows: <path d="M6 4a2 2 0 1 1 0 4 2 2 0 0 1 0-4zM18 16a2 2 0 1 1 0 4 2 2 0 0 1 0-4zM6 8v6a2 2 0 0 0 2 2h8" />,
  crm: <path d="M16 20v-2a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v2M9.5 10a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM21 20v-2a4 4 0 0 0-3-3.9M16.5 4.1a4 4 0 0 1 0 7.8" />,
  outreach: <path d="M4 5h16v14H4zM4 6l8 6 8-6" />,
  inbox: <path d="M4 4h16v16H4zM4 13h4l2 3h4l2-3h4" />,
  projects: <path d="M4 6h5l2 2h9v10H4zM4 6v12" />,
  'ai-assistant': <path d="M12 3l1.6 3.6L17 8l-3.4 1.4L12 13l-1.6-3.6L7 8l3.4-1.4zM18 14l.8 1.8L21 17l-2.2.8L18 20l-.8-2.2L15 17l2.2-.8z" />,
  // Équipe IA — a friendly agent/robot head with antenna + side sensors.
  'ai-team': <path d="M12 2v2M7 6h10a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2zM9 12h.01M15 12h.01M9.5 15.5h5M3 10v4M21 10v4" />,
  // Orchestration — connected nodes converging (a plan DAG fanning to a result).
  orchestration: <path d="M5 6a2 2 0 1 0 0-4 2 2 0 0 0 0 4zM19 6a2 2 0 1 0 0-4 2 2 0 0 0 0 4zM12 22a2 2 0 1 0 0-4 2 2 0 0 0 0 4zM5 6v4a2 2 0 0 0 2 2h1M19 6v4a2 2 0 0 1-2 2h-1M9 12h6M12 12v6" />,
  studio: <path d="M3 9h18v10H3zM3 9l1.5-4h15L21 9M8 5 6.5 9M13 5l-1.5 4M18 5l-1.5 4" />,
  repurpose: <path d="M4 9a8 8 0 0 1 13-3l2 2M20 15a8 8 0 0 1-13 3l-2-2M17 4v4h-4M7 20v-4h4" />,
  hooks: <path d="M14 4h3v6a5 5 0 0 1-10 0M14 4v8a2 2 0 1 1-4 0" />,
  voice: <path d="M4 10v4M8 6v12M12 8v8M16 5v14M20 10v4" />,
  'ab-test': <path d="M9 3h6M10 3v6l-5 9a2 2 0 0 0 2 3h10a2 2 0 0 0 2-3l-5-9V3M7 16h10" />,
  suggestions: <path d="M9 18h6M10 21h4M12 3a6 6 0 0 0-4 10c1 1 1.5 2 1.5 3h5c0-1 .5-2 1.5-3a6 6 0 0 0-4-10z" />,
  personalization: <path d="M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z" />,
  community: <path d="M8 10h8M8 14h5M21 12a8 8 0 0 1-11.3 7.3L3 21l1.7-6.7A8 8 0 1 1 21 12z" />,
  documents: <path d="M8 4h6l4 4v10a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2zM14 4v4h4M9 13h6M9 16h4" />,
  memory: <path d="M9 3a3 3 0 0 0-3 3 3 3 0 0 0-1 5.8A3 3 0 0 0 6 17a3 3 0 0 0 6 .9V6a3 3 0 0 0-3-3zM15 3a3 3 0 0 1 3 3 3 3 0 0 1 1 5.8A3 3 0 0 1 18 17a3 3 0 0 1-6 .9" />,
  approvals: <path d="M4 12a8 8 0 1 0 16 0 8 8 0 0 0-16 0zM9 12l2 2 4-4" />,
  'ai-activity': <path d="M3 12h4l2 6 4-14 2 8h6" />,
  marketplace: <path d="M4 9h16l-1-4H5zM5 9v11h14V9M9 20v-6h6v6" />,
  // Base B2B — stacked database cylinders.
  prospect: <path d="M4 6c0-1.1 3.6-2 8-2s8 .9 8 2-3.6 2-8 2-8-.9-8-2zM4 6v12c0 1.1 3.6 2 8 2s8-.9 8-2V6M4 12c0 1.1 3.6 2 8 2s8-.9 8-2" />,
  // Découverte — magnifier over a geo pin (geo-targeted discovery).
  discovery: <path d="M12 21s7-6.5 7-11.5a7 7 0 1 0-14 0C5 14.5 12 21 12 21zM9.5 9.5a2.5 2.5 0 1 0 5 0 2.5 2.5 0 0 0-5 0z" />,
  // Social leads — connected nodes.
  social: <path d="M18 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM6 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM18 22a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM8.6 10.5l6.8-3.9M8.6 13.5l6.8 3.9" />,
  // Social listening — broadcast / signal waves.
  listening: <path d="M4.9 4.9a10 10 0 0 0 0 14.2M7.8 7.8a6 6 0 0 0 0 8.4M19.1 4.9a10 10 0 0 1 0 14.2M16.2 7.8a6 6 0 0 1 0 8.4M12 13.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3z" />,
  // Ads — megaphone.
  ads: <path d="M3 11v2a1 1 0 0 0 1 1h2l3.5 3.5a1 1 0 0 0 1.7-.7V7.2a1 1 0 0 0-1.7-.7L6 10H4a1 1 0 0 0-1 1zM15 8a4 4 0 0 1 0 8M7 18l1 3" />,
  // Link-in-bio — chain link.
  bio: <path d="M9 12h6M8.5 8H7a4 4 0 0 0 0 8h1.5M15.5 8H17a4 4 0 0 1 0 8h-1.5" />,
  settings: <path d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM19.4 13a1.6 1.6 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.6 1.6 0 0 0-2.7 1.1V21a2 2 0 1 1-4 0v-.1A1.6 1.6 0 0 0 7 19.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.6 1.6 0 0 0-1.1-2.7H3a2 2 0 1 1 0-4h.1A1.6 1.6 0 0 0 4.7 7l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.6 1.6 0 0 0 1.8.3H9.4A1.6 1.6 0 0 0 11 3.1V3a2 2 0 1 1 4 0v.1a1.6 1.6 0 0 0 2.7 1.1l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.6 1.6 0 0 0-.3 1.8V9.4a1.6 1.6 0 0 0 1.7 1.6H21a2 2 0 1 1 0 4h-.1a1.6 1.6 0 0 0-1.5 1z" />,
};

function NavIcon({ name, className = 'h-5 w-5' }: { name: IconName; className?: string }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      {ICON_PATHS[name]}
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Navigation model.
// ---------------------------------------------------------------------------

// Nav labels are stored as i18n keys (nav.*) and resolved with `t(...)` at
// render, so the whole sidebar follows the active locale (and mirrors in RTL).
interface NavItem {
  to: string;
  labelKey: TranslationKey;
  /**
   * Optional literal label that overrides the i18n key. Used for a brand-new
   * item whose catalog key does not exist yet (e.g. "Équipe 24/7"); everything
   * else stays i18n-driven via `labelKey`.
   */
  label?: string;
  icon: IconName;
}

const NAV_GROUPS: { labelKey: TranslationKey; items: NavItem[] }[] = [
  {
    labelKey: 'nav.groups.general',
    items: [
      { to: '/', labelKey: 'nav.items.dashboard', icon: 'dashboard' },
      { to: '/content', labelKey: 'nav.items.content', icon: 'content' },
      { to: '/calendar', labelKey: 'nav.items.calendar', icon: 'calendar' },
      { to: '/publishing', labelKey: 'nav.items.publishing', icon: 'publishing' },
      { to: '/publishing/queue', labelKey: 'nav.items.contentQueue', icon: 'publishing' },
      { to: '/publishing/bulk', labelKey: 'nav.items.bulkSchedule', icon: 'publishing' },
      { to: '/platforms', labelKey: 'nav.items.platforms', icon: 'platforms' },
      { to: '/ads', labelKey: 'nav.items.ads', icon: 'ads' },
      { to: '/media-library', labelKey: 'nav.items.mediaLibrary', icon: 'media' },
      { to: '/analytics', labelKey: 'nav.items.analytics', icon: 'analytics' },
    ],
  },
  {
    labelKey: 'nav.groups.creation',
    items: [
      { to: '/design', labelKey: 'nav.items.design', icon: 'design' },
      { to: '/content/carousel', labelKey: 'nav.items.carouselEditor', icon: 'design' },
      { to: '/content/studio', labelKey: 'nav.items.richContentStudio', icon: 'studio' },
      { to: '/workflows', labelKey: 'nav.items.workflows', icon: 'workflows' },
    ],
  },
  {
    labelKey: 'nav.groups.prospection',
    items: [
      { to: '/crm', labelKey: 'nav.items.crm', icon: 'crm' },
      { to: '/prospection', labelKey: 'nav.items.discovery', icon: 'discovery' },
      { to: '/prospection/recommend', labelKey: 'nav.items.prospectionRecommend', icon: 'prospect' },
      { to: '/prospection/calendar', labelKey: 'nav.items.prospectionCalendar', icon: 'calendar' },
      { to: '/prospection/account-intelligence', labelKey: 'nav.items.accountIntelligence', icon: 'prospect' },
      { to: '/prospect', labelKey: 'nav.items.prospect', icon: 'prospect' },
      { to: '/marketplace', labelKey: 'nav.items.marketplace', icon: 'marketplace' },
      { to: '/social', labelKey: 'nav.items.social', icon: 'social' },
      { to: '/outreach', labelKey: 'nav.items.outreach', icon: 'outreach' },
      { to: '/inbox', labelKey: 'nav.items.inbox', icon: 'inbox' },
      { to: '/community', labelKey: 'nav.items.community', icon: 'community' },
      { to: '/listening', labelKey: 'nav.items.listening', icon: 'listening' },
      { to: '/bio', labelKey: 'nav.items.bio', icon: 'bio' },
      { to: '/projects', labelKey: 'nav.items.projects', icon: 'projects' },
    ],
  },
  {
    labelKey: 'nav.groups.ai',
    items: [
      { to: '/ai-team/hq', label: 'Équipe 24/7', labelKey: 'nav.items.aiTeam', icon: 'ai-team' },
      { to: '/ai-team', labelKey: 'nav.items.aiTeam', icon: 'ai-team' },
      { to: '/ai-team/office', labelKey: 'nav.items.aiOffice', icon: 'ai-team' },
      { to: '/ai-team/teams', labelKey: 'nav.items.aiTeams', icon: 'ai-team' },
      { to: '/ai-team/deliverables', labelKey: 'nav.items.aiDeliverables', icon: 'ai-team' },
      { to: '/ai-team/schedules', labelKey: 'nav.items.aiSchedules', icon: 'ai-team' },
      { to: '/ai-team/control', labelKey: 'nav.items.aiControl', icon: 'ai-team' },
      { to: '/ai-team/audit', labelKey: 'nav.items.aiAuditTrail', icon: 'ai-team' },
      { to: '/orchestration', labelKey: 'nav.items.orchestration', icon: 'orchestration' },
      { to: '/ai-assistant', labelKey: 'nav.items.aiAssistant', icon: 'ai-assistant' },
      { to: '/ai/content-studio', labelKey: 'nav.items.contentStudio', icon: 'studio' },
      { to: '/ai/ghostwriter', labelKey: 'nav.items.ghostwriter', icon: 'studio' },
      { to: '/ai/repurpose', labelKey: 'nav.items.repurpose', icon: 'repurpose' },
      { to: '/ai/hook-library', labelKey: 'nav.items.hookLibrary', icon: 'hooks' },
      { to: '/ai/brand-voice', labelKey: 'nav.items.brandVoice', icon: 'voice' },
      { to: '/ai/ab-copy', labelKey: 'nav.items.abCopy', icon: 'ab-test' },
      { to: '/ai/suggestions', labelKey: 'nav.items.suggestions', icon: 'suggestions' },
      { to: '/ai/personalization', labelKey: 'nav.items.personalization', icon: 'personalization' },
      { to: '/ai/community-ai', labelKey: 'nav.items.communityAi', icon: 'community' },
      { to: '/ai/rag-documents', labelKey: 'nav.items.ragDocuments', icon: 'documents' },
      { to: '/ai/memory', labelKey: 'nav.items.memory', icon: 'memory' },
      { to: '/approvals', labelKey: 'nav.items.approvals', icon: 'approvals' },
      { to: '/staff-questions', labelKey: 'nav.items.staffQuestions', icon: 'approvals' },
      { to: '/grants', labelKey: 'nav.items.grants', icon: 'approvals' },
      { to: '/superadmin', labelKey: 'nav.items.superadminFleet', icon: 'ai-team' },
      { to: '/ai-activity', labelKey: 'nav.items.aiActivity', icon: 'ai-activity' },
    ],
  },
  {
    labelKey: 'nav.groups.system',
    items: [{ to: '/settings', labelKey: 'nav.items.settings', icon: 'settings' }],
  },
];

// Quick-create shortcuts surfaced in the header "＋ Créer" menu.
// `?new=1` is the create-intent flag (see useCreateIntent): the target list
// page opens its own create modal on land instead of just showing the list.
const QUICK_CREATE: { labelKey: TranslationKey; to: string; icon: IconName; hintKey: TranslationKey }[] = [
  { labelKey: 'nav.quickCreate.newContent', to: '/content/new', icon: 'content', hintKey: 'nav.items.content' },
  { labelKey: 'nav.quickCreate.newContact', to: '/crm/contacts?new=1', icon: 'crm', hintKey: 'nav.items.crm' },
  { labelKey: 'nav.quickCreate.newCompany', to: '/crm/companies?new=1', icon: 'crm', hintKey: 'nav.items.crm' },
  { labelKey: 'nav.quickCreate.newCampaign', to: '/outreach/campaigns?new=1', icon: 'outreach', hintKey: 'nav.items.outreach' },
  { labelKey: 'nav.quickCreate.newProject', to: '/projects?new=1', icon: 'projects', hintKey: 'nav.items.projects' },
  { labelKey: 'nav.quickCreate.newDesign', to: '/design', icon: 'design', hintKey: 'nav.items.design' },
];

// ---------------------------------------------------------------------------
// Sidebar group collapse — persisted per browser (set of collapsed group
// labels). Kept out of the global UI store so it stays a shell-local concern.
// ---------------------------------------------------------------------------

const NAV_GROUPS_KEY = 'psn-nav-collapsed-groups';

function loadCollapsedGroups(): Set<string> {
  try {
    const raw = localStorage.getItem(NAV_GROUPS_KEY);
    const arr: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr)
      ? new Set(arr.filter((x): x is string => typeof x === 'string'))
      : new Set();
  } catch {
    return new Set();
  }
}

function saveCollapsedGroups(groups: Set<string>): void {
  try {
    localStorage.setItem(NAV_GROUPS_KEY, JSON.stringify([...groups]));
  } catch {
    /* ignore quota / private-mode failures */
  }
}

// ---------------------------------------------------------------------------
// Header controls.
// ---------------------------------------------------------------------------

/** Header "＋ Créer" dropdown of quick-create actions. */
function QuickCreateMenu() {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        // Distinct accessible name: the visible label is just "Créer", which on
        // pages like /content/new collides with that page's own "Créer" submit
        // button (two same-named controls → ambiguous by-name selection swallowed
        // the create). A specific name also gives this icon-only trigger a name
        // on mobile, where the "Créer" span is hidden. Keeps "Créer" as a
        // substring (WCAG 2.5.3 Label-in-Name).
        aria-label={t('nav.header.createItem')}
        className="flex items-center gap-1.5 rounded-token bg-gradient-to-br from-primary to-accent px-2.5 py-1.5 text-sm font-medium text-primary-foreground shadow-elevation-1 transition hover:brightness-110 hover:shadow-elevation-2 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-surface"
      >
        <svg aria-hidden="true" viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 5v14M5 12h14" />
        </svg>
        <span className="hidden sm:inline">{t('nav.header.create')}</span>
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full z-50 mt-1 w-60 overflow-hidden rounded-token-lg border border-border bg-surface py-1 shadow-elevation-3"
        >
          <div className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wide text-muted">
            {t('nav.header.create')}
          </div>
          {QUICK_CREATE.map((item) => (
            <button
              key={item.to + item.labelKey}
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                void navigate(item.to);
              }}
              className="flex w-full items-center gap-3 px-3 py-2 text-left text-sm text-foreground hover:bg-surface-2"
            >
              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-token bg-surface-2 text-primary">
                <NavIcon name={item.icon} className="h-4 w-4" />
              </span>
              <span className="min-w-0 flex-1 truncate">{t(item.labelKey)}</span>
              <span className="shrink-0 text-[11px] text-muted">{t(item.hintKey)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Quick-access toolbar actions — the most-used jumps, reusing the same
// create-intent routes as the ＋Créer menu / ⌘K palette. Rendered as an icon
// bar on lg+ and folded into a single "⋯" overflow menu on smaller screens.
interface QuickAction {
  key: string;
  labelKey: TranslationKey;
  icon: ReactNode;
  run: (navigate: ReturnType<typeof useNavigate>, onSearch: () => void) => void;
}

const BellIcon = (
  <svg aria-hidden="true" viewBox="0 0 24 24" className="h-[18px] w-[18px]" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9M10.3 21a2 2 0 0 0 3.4 0" />
  </svg>
);
const DealIcon = (
  <svg aria-hidden="true" viewBox="0 0 24 24" className="h-[18px] w-[18px]" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2v20M8 6h5.5a2.5 2.5 0 0 1 0 5H8h8a2.5 2.5 0 0 1 0 5H6" />
  </svg>
);
const SearchIcon = (
  <svg aria-hidden="true" viewBox="0 0 24 24" className="h-[18px] w-[18px]" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-3.2-3.2" />
  </svg>
);

const QUICK_ACCESS: QuickAction[] = [
  { key: 'search', labelKey: 'nav.quickAccess.search', icon: SearchIcon, run: (_n, onSearch) => onSearch() },
  { key: 'content', labelKey: 'nav.quickAccess.newContent', icon: <NavIcon name="content" className="h-[18px] w-[18px]" />, run: (n) => void n('/content/new') },
  { key: 'contact', labelKey: 'nav.quickAccess.newContact', icon: <NavIcon name="crm" className="h-[18px] w-[18px]" />, run: (n) => void n('/crm/contacts?new=1') },
  { key: 'deal', labelKey: 'nav.quickAccess.newDeal', icon: DealIcon, run: (n) => void n('/crm/pipeline') },
  { key: 'notifications', labelKey: 'nav.quickAccess.inbox', icon: BellIcon, run: (n) => void n('/inbox') },
  { key: 'copilot', labelKey: 'nav.quickAccess.assistant', icon: <NavIcon name="ai-assistant" className="h-[18px] w-[18px]" />, run: (n) => void n('/ai-assistant') },
];

function QuickAccessToolbar({ onSearch }: { onSearch: () => void }) {
  const { t } = useT();
  const navigate = useNavigate();
  const [overflowOpen, setOverflowOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!overflowOpen) return undefined;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOverflowOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOverflowOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [overflowOpen]);

  return (
    <>
      {/* Full icon bar — desktop. A subtle grouped "pill" of quick actions. */}
      <div className="hidden items-center gap-0.5 rounded-token border border-border bg-surface-2/40 p-0.5 shadow-elevation-1 lg:flex">
        {/* `search` is omitted here — the wide search box sits right beside it. */}
        {QUICK_ACCESS.filter((a) => a.key !== 'search').map((a) => (
          <button
            key={a.key}
            type="button"
            onClick={() => a.run(navigate, onSearch)}
            title={t(a.labelKey)}
            aria-label={t(a.labelKey)}
            className="um-icon-btn h-8 w-8"
          >
            {a.icon}
          </button>
        ))}
      </div>

      {/* Overflow menu — tablet / mobile (single button, never overflows). */}
      <div className="relative lg:hidden" ref={ref}>
        <button
          type="button"
          onClick={() => setOverflowOpen((v) => !v)}
          aria-haspopup="menu"
          aria-expanded={overflowOpen}
          aria-label={t('nav.header.quickActions')}
          title={t('nav.header.quickActions')}
          className="um-icon-btn h-9 w-9"
        >
          <svg aria-hidden="true" viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor">
            <circle cx="5" cy="12" r="1.6" />
            <circle cx="12" cy="12" r="1.6" />
            <circle cx="19" cy="12" r="1.6" />
          </svg>
        </button>
        {overflowOpen && (
          <div
            role="menu"
            className="absolute right-0 top-full z-50 mt-1 w-56 overflow-hidden rounded-token-lg border border-border bg-surface py-1 shadow-elevation-3"
          >
            <div className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wide text-muted">
              {t('nav.header.quickActions')}
            </div>
            {QUICK_ACCESS.map((a) => (
              <button
                key={a.key}
                type="button"
                role="menuitem"
                onClick={() => {
                  setOverflowOpen(false);
                  a.run(navigate, onSearch);
                }}
                className="flex w-full items-center gap-3 px-3 py-2 text-left text-sm text-foreground hover:bg-surface-2"
              >
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-token bg-surface-2 text-primary">
                  {a.icon}
                </span>
                <span className="min-w-0 flex-1 truncate">{t(a.labelKey)}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

function UserMenu({ displayName, onSignout }: { displayName: string; onSignout: () => void }) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const initials = displayName.trim().charAt(0).toUpperCase() || 'U';

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        className="flex items-center gap-2 rounded-token p-1 pr-2 text-sm text-foreground transition-colors hover:bg-surface-2 focus:outline-none focus:ring-2 focus:ring-ring"
      >
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-primary to-accent text-xs font-semibold text-primary-foreground shadow-elevation-1 ring-1 ring-inset ring-white/20">
          {initials}
        </span>
        <span className="hidden max-w-[10rem] truncate sm:inline">{displayName}</span>
        <svg aria-hidden="true" viewBox="0 0 24 24" className="hidden h-4 w-4 text-muted sm:inline" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>

      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full z-50 mt-1 w-56 overflow-hidden rounded-token-lg border border-border bg-surface py-1 shadow-elevation-2"
        >
          <div className="border-b border-border px-3 py-2">
            <div className="truncate text-sm font-medium text-foreground">{displayName}</div>
            <div className="text-xs text-muted">{t('nav.user.connectedAccount')}</div>
          </div>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              void navigate('/settings/profile');
            }}
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-foreground hover:bg-surface-2"
          >
            <NavIcon name="settings" className="h-4 w-4" />
            {t('nav.user.settings')}
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onSignout();
            }}
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-danger hover:bg-surface-2"
          >
            <svg aria-hidden="true" viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 4h3a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-3M10 17l5-5-5-5M15 12H3" />
            </svg>
            {t('nav.user.signout')}
          </button>
        </div>
      )}
    </div>
  );
}

function readEnv(): string {
  try {
    const cfg = getConfig();
    if (cfg.env) return cfg.env;
    if (/localhost|127\.0\.0\.1/.test(cfg.apiBaseUrl)) return 'local';
    return 'prod';
  } catch {
    return 'local';
  }
}

// ---------------------------------------------------------------------------
// Shell.
// ---------------------------------------------------------------------------

type ApiHealth = 'checking' | 'up' | 'down';

/**
 * Poll the gateway /health so the footer status dot reflects reality instead of
 * a hardcoded green "Opérationnel" (BUG-03: it stayed green even during a total
 * backend 503). /health is public (no auth) and CORS-enabled; a network failure
 * or non-2xx (e.g. the nginx 503 when the upstream is down) reads as "down".
 */
function useApiHealth(): ApiHealth {
  const [health, setHealth] = useState<ApiHealth>('checking');
  useEffect(() => {
    let alive = true;
    const check = async () => {
      const ctrl = new AbortController();
      const timer = window.setTimeout(() => ctrl.abort(), 8000);
      try {
        const { apiBaseUrl } = getConfig();
        const res = await fetch(`${apiBaseUrl}/health`, { signal: ctrl.signal });
        if (alive) setHealth(res.ok ? 'up' : 'down');
      } catch {
        if (alive) setHealth('down');
      } finally {
        window.clearTimeout(timer);
      }
    };
    void check();
    const id = window.setInterval(check, 30000);
    return () => {
      alive = false;
      window.clearInterval(id);
    };
  }, []);
  return health;
}

export function DashboardLayout() {
  const auth = useAuth();
  const { t } = useT();
  const { sidebarCollapsed, toggleSidebar } = useUIStore();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(loadCollapsedGroups);
  const apiHealth = useApiHealth();

  const toggleGroup = (label: string) =>
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      saveCollapsedGroups(next);
      return next;
    });

  // Global keyboard layer: `?` opens the cheat-sheet, `/` opens the palette,
  // `g <key>` jumps between sections. (Cmd/Ctrl-K stays owned by the palette.)
  useGlobalShortcuts({
    onHelp: () => setShortcutsOpen(true),
    onOpenPalette: () => setPaletteOpen(true),
  });

  const displayName =
    auth.user?.profile?.name ??
    auth.user?.profile?.preferred_username ??
    t('nav.user.fallbackName');
  const env = readEnv();

  // Current top-level section label (longest matching nav route) for the header
  // breadcrumb. Derived from the URL — no route-file coupling.
  const { pathname } = useLocation();
  const activeLabel = (() => {
    let best: NavItem | undefined;
    for (const item of NAV_GROUPS.flatMap((g) => g.items)) {
      if (item.to === '/') {
        if (pathname === '/') best = item;
        continue;
      }
      if (pathname === item.to || pathname.startsWith(`${item.to}/`)) {
        if (!best || item.to.length > best.to.length) best = item;
      }
    }
    return best ? (best.label ?? t(best.labelKey)) : '';
  })();

  // Close the mobile drawer on Escape.
  useEffect(() => {
    if (!mobileOpen) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMobileOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [mobileOpen]);

  const renderNav = (collapsed: boolean) => (
    <nav className="flex-1 overflow-y-auto overflow-x-hidden p-2">
      {NAV_GROUPS.map((group) => {
        // Groups collapse only in the expanded rail (the icon rail always shows
        // every item so nothing becomes unreachable). Keyed by the stable i18n
        // key (not the translated text) so collapse state survives a language
        // switch.
        const groupOpen = collapsed || !collapsedGroups.has(group.labelKey);
        return (
        <div key={group.labelKey} className="mb-4">
          {!collapsed && (
            <button
              type="button"
              onClick={() => toggleGroup(group.labelKey)}
              aria-expanded={groupOpen}
              className="group/gh flex w-full items-center justify-between rounded-token px-2 py-1 text-xs font-semibold uppercase tracking-wide text-muted transition-colors hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <span>{t(group.labelKey)}</span>
              <svg
                aria-hidden="true"
                viewBox="0 0 24 24"
                className={`h-3.5 w-3.5 shrink-0 transition-all duration-200 group-hover/gh:opacity-100 group-focus-visible/gh:opacity-100 ${
                  groupOpen ? 'opacity-0 group-hover/gh:rotate-0' : '-rotate-90 opacity-100'
                }`}
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="m6 9 6 6 6-6" />
              </svg>
            </button>
          )}
          {groupOpen &&
          group.items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              onClick={() => setMobileOpen(false)}
              title={collapsed ? (item.label ?? t(item.labelKey)) : undefined}
              className={({ isActive }) =>
                `group relative flex items-center gap-3 rounded-token px-2 py-2 text-sm transition-colors ${
                  collapsed ? 'justify-center' : ''
                } ${
                  isActive
                    ? 'bg-gradient-to-r from-primary/[0.18] to-accent/[0.06] font-semibold text-primary shadow-[inset_0_1px_0_0_rgb(var(--color-primary)/0.16)]'
                    : 'text-muted hover:bg-surface-2 hover:text-foreground'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {isActive && (
                    <span
                      aria-hidden="true"
                      className={`absolute left-0 top-1/2 h-5 -translate-y-1/2 rounded-r-full bg-gradient-to-b from-primary to-accent shadow-[0_0_8px_rgb(var(--color-primary)/0.55)] ${
                        collapsed ? 'w-0.5' : 'w-1'
                      }`}
                    />
                  )}
                  <NavIcon
                    name={item.icon}
                    className={`h-5 w-5 shrink-0 transition-transform duration-200 group-hover:scale-110 motion-reduce:transition-none motion-reduce:group-hover:scale-100 ${
                      collapsed ? '' : ''
                    }`}
                  />
                  {!collapsed && <span className="truncate">{item.label ?? t(item.labelKey)}</span>}
                  {collapsed && (
                    <span
                      role="tooltip"
                      className="pointer-events-none absolute left-full top-1/2 z-50 ml-2 -translate-y-1/2 whitespace-nowrap rounded-token border border-border bg-surface px-2 py-1 text-xs text-foreground opacity-0 shadow-elevation-2 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
                    >
                      {item.label ?? t(item.labelKey)}
                    </span>
                  )}
                </>
              )}
            </NavLink>
          ))}
        </div>
        );
      })}
    </nav>
  );

  const brand = (collapsed: boolean) => (
    <div className="flex h-14 items-center gap-2 border-b border-border px-4">
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-token bg-gradient-to-br from-primary to-accent text-sm font-bold text-primary-foreground shadow-elevation-1 ring-1 ring-inset ring-white/20">
        P
      </span>
      {!collapsed && (
        <span className="bg-gradient-to-r from-foreground to-foreground/70 bg-clip-text text-base font-bold tracking-tight text-transparent">
          {t('nav.brand')}
        </span>
      )}
    </div>
  );

  return (
    <div className="flex h-screen overflow-hidden bg-bg text-foreground">
      {/* Desktop rail */}
      <aside
        className={`hidden shrink-0 flex-col border-r border-border bg-surface transition-all md:flex ${
          sidebarCollapsed ? 'w-16' : 'w-60'
        }`}
      >
        {brand(sidebarCollapsed)}
        {renderNav(sidebarCollapsed)}
      </aside>

      {/* Mobile drawer + backdrop */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-slate-950/50 md:hidden"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}
      <aside
        className={`fixed inset-y-0 left-0 z-50 flex w-64 flex-col border-r border-border bg-surface shadow-elevation-3 transition-transform md:hidden ${
          mobileOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        aria-label={t('nav.primaryNav')}
        aria-hidden={!mobileOpen}
      >
        <div className="flex h-14 items-center justify-between border-b border-border px-4">
          <span className="flex items-center gap-2">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-token bg-gradient-to-br from-primary to-accent text-sm font-bold text-primary-foreground shadow-elevation-1 ring-1 ring-inset ring-white/20">
              P
            </span>
            <span className="text-base font-bold tracking-tight text-foreground">{t('nav.brand')}</span>
          </span>
          <button
            type="button"
            onClick={() => setMobileOpen(false)}
            aria-label={t('nav.header.closeMenu')}
            className="rounded-token p-2 text-muted hover:bg-surface-2 hover:text-foreground"
          >
            <svg aria-hidden="true" viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 6l12 12M18 6 6 18" />
            </svg>
          </button>
        </div>
        {renderNav(false)}
      </aside>

      {/* Main column */}
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <header className="um-glass relative z-20 flex h-14 shrink-0 items-center justify-between gap-2 border-b border-border px-3 shadow-elevation-1 sm:px-4">
          <div className="flex min-w-0 items-center gap-1">
            {/* Mobile: open drawer */}
            <button
              type="button"
              onClick={() => setMobileOpen(true)}
              className="rounded-token p-2 text-muted hover:bg-surface-2 hover:text-foreground md:hidden"
              aria-label={t('nav.header.openMenu')}
            >
              <svg aria-hidden="true" viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            {/* Desktop: collapse rail */}
            <button
              type="button"
              onClick={toggleSidebar}
              className="hidden rounded-token p-2 text-muted hover:bg-surface-2 hover:text-foreground md:inline-flex"
              aria-label={t('nav.header.toggleSidebar')}
            >
              <svg aria-hidden="true" viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>

            {/* Breadcrumb / current section (derived from the URL). */}
            {activeLabel && (
              <nav aria-label={t('nav.header.breadcrumbAria')} className="ml-1 hidden min-w-0 items-center md:flex">
                <span className="shrink-0 text-sm text-muted">{t('nav.header.breadcrumbRoot')}</span>
                <svg aria-hidden="true" viewBox="0 0 24 24" className="mx-1 h-4 w-4 shrink-0 text-muted/60" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="m9 6 6 6-6 6" />
                </svg>
                <span aria-current="page" className="truncate text-sm font-semibold text-foreground">
                  {activeLabel}
                </span>
              </nav>
            )}
          </div>

          <div className="flex items-center gap-1 sm:gap-2">
            <button
              type="button"
              onClick={() => setPaletteOpen(true)}
              className="flex items-center gap-2 rounded-token border border-border bg-surface-2/40 px-2 py-1.5 text-sm text-muted transition-colors hover:border-primary/40 hover:bg-surface-2 hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring lg:min-w-[13rem]"
              aria-label={t('nav.header.openCommandPalette')}
            >
              <svg aria-hidden="true" viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="7" />
                <path d="m20 20-3.2-3.2" />
              </svg>
              <span className="hidden lg:inline">{t('nav.header.searchPlaceholder')}</span>
              <kbd className="ml-auto hidden rounded border border-border bg-surface px-1.5 py-0.5 text-[10px] font-medium lg:inline">
                ⌘K
              </kbd>
            </button>

            <QuickAccessToolbar onSearch={() => setPaletteOpen(true)} />

            <QuickCreateMenu />

            <button
              type="button"
              onClick={() => setShortcutsOpen(true)}
              aria-label={t('nav.header.showShortcuts')}
              aria-keyshortcuts="?"
              title={`${t('nav.header.shortcuts')} (?)`}
              className="hidden rounded-token p-2 text-muted transition-colors hover:bg-surface-2 hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring sm:inline-flex"
            >
              <svg aria-hidden="true" viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="9" />
                <path d="M9.5 9a2.5 2.5 0 1 1 3.5 2.3c-.7.3-1 .7-1 1.4v.3" />
                <path d="M12 17h.01" />
              </svg>
            </button>

            <NotificationBell />

            <ThemePicker />

            <UserMenu displayName={displayName} onSignout={() => void auth.signoutRedirect()} />
          </div>
        </header>

        <UpdateBanner />

        <main className="flex-1 overflow-y-auto overflow-x-hidden">
          {/* Keyed by pathname so a gentle fade/rise plays on every route
              change (CSS handles reduced-motion by dropping the transform). */}
          <div key={pathname} className="um-animate-page min-h-full p-4 sm:p-6">
            {/* Keyed by pathname (parent div) so a page crash resets on nav.
                Contains render errors to this panel — the shell stays alive. */}
            <RouteErrorBoundary>
              <Outlet />
            </RouteErrorBoundary>
          </div>
        </main>

        <footer className="um-glass flex shrink-0 flex-wrap items-center justify-between gap-x-4 gap-y-1 border-t border-border px-4 py-2 text-xs text-muted">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="font-medium text-foreground">{t('nav.brand')}</span>
            <span aria-hidden="true">·</span>
            <Link
              to="/versionsbuilt"
              className="rounded-token-sm text-muted transition-colors hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              title="Provenance de build (version, commit, date)"
            >
              v{APP_VERSION}
            </Link>
            <span
              className="rounded-full bg-surface-2 px-1.5 py-0.5 font-medium uppercase tracking-wide"
              title={t('nav.footer.environment')}
            >
              {env}
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
            <nav aria-label={t('nav.footer.links')} className="flex items-center gap-x-4">
              <Link
                to="/versionsbuilt"
                className="rounded-token-sm transition-colors hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              >
                {t('nav.footer.whatsNew')}
              </Link>
              <button
                type="button"
                onClick={() => setShortcutsOpen(true)}
                className="rounded-token-sm transition-colors hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              >
                {t('nav.footer.shortcuts')}
              </button>
              <Link
                to="/confidentialite"
                className="rounded-token-sm transition-colors hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              >
                {t('nav.footer.privacy')}
              </Link>
            </nav>
            <span
              className="flex items-center gap-1.5"
              title={t('nav.footer.statusTitle')}
            >
              <span className="relative flex h-2 w-2" aria-hidden="true">
                {apiHealth === 'up' && (
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-success opacity-60 motion-reduce:hidden" />
                )}
                <span
                  className={`relative inline-flex h-2 w-2 rounded-full ${
                    apiHealth === 'up'
                      ? 'bg-success'
                      : apiHealth === 'down'
                        ? 'bg-danger'
                        : 'bg-warning'
                  }`}
                />
              </span>
              <span>
                {apiHealth === 'up'
                  ? t('nav.footer.operational')
                  : apiHealth === 'down'
                    ? t('nav.footer.unavailable')
                    : t('nav.footer.checking')}
              </span>
            </span>
          </div>
        </footer>
      </div>

      <CommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
      <ShortcutsOverlay open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />
      <Toaster />
      <GlobalConfirmHost />
      <CopilotProvider />
      <DemoTour />
    </div>
  );
}
